import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import translations from './translations';

const LanguageContext = createContext({
    language: 'fr',
    setLanguage: () => {},
    t: (key) => key,
    availableLanguages: ['fr', 'en']
});

const supportedLanguages = ['fr', 'en'];
const fallbackLanguage = 'fr';
const storageKey = 'maf-language';

const normalizeLanguage = (language) => {
    if (!language) {
        return fallbackLanguage;
    }
    const normalized = language.toLowerCase();
    const short = normalized.split('-')[0];
    return supportedLanguages.includes(short) ? short : fallbackLanguage;
};

const getInitialLanguage = () => {
    if (typeof window === 'undefined') {
        return fallbackLanguage;
    }
    const stored = window.localStorage.getItem(storageKey);
    if (stored && supportedLanguages.includes(stored)) {
        return stored;
    }
    return normalizeLanguage(window.navigator.language);
};

const getNestedValue = (obj, path) => {
    if (!obj || !path) {
        return undefined;
    }
    return path.split('.').reduce((acc, key) => {
        if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
            return acc[key];
        }
        return undefined;
    }, obj);
};

const interpolate = (value, vars) => {
    if (!vars) {
        return value;
    }
    return value.replace(/\{(\w+)\}/g, (match, key) => {
        if (Object.prototype.hasOwnProperty.call(vars, key)) {
            return vars[key];
        }
        return match;
    });
};

const translate = (language, key, vars) => {
    const primary = getNestedValue(translations[language], key);
    const fallback = getNestedValue(translations[fallbackLanguage], key);
    const value = primary ?? fallback;
    if (typeof value === 'string') {
        return interpolate(value, vars);
    }
    return value ?? key;
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(getInitialLanguage);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.localStorage.setItem(storageKey, language);
        document.documentElement.lang = language;
    }, [language]);

    const t = useMemo(() => {
        return (key, vars) => translate(language, key, vars);
    }, [language]);

    const value = useMemo(
        () => ({
            language,
            setLanguage,
            t,
            availableLanguages: supportedLanguages
        }),
        [language, t]
    );

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => useContext(LanguageContext);
